<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import agentsAPI from 'dashboard/api/agents';
import kanbanSharedAccessAPI from 'dashboard/api/kanbanSharedAccess';
import { kanbanAccessErrorKey } from 'dashboard/helper/kanbanSharedAccess';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  itemTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'updated']);
const { t } = useI18n();

const dialogRef = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const isCopying = ref(false);
const errorKey = ref('');
const operationErrorKey = ref('');
const copyState = ref('');
const shareUrl = ref('');
const canManage = ref(false);
const accountAgents = ref([]);
const originalAccesses = ref([]);
const draftAccesses = ref([]);
const selectedUserId = ref('');
const selectedAccessLevel = ref('comment');

const levels = computed(() => [
  {
    id: 'view',
    label: t('KANBAN_SHARING.LEVELS.VIEW'),
    help: t('KANBAN_SHARING.LEVEL_HELP.VIEW'),
  },
  {
    id: 'comment',
    label: t('KANBAN_SHARING.LEVELS.COMMENT'),
    help: t('KANBAN_SHARING.LEVEL_HELP.COMMENT'),
  },
  {
    id: 'edit',
    label: t('KANBAN_SHARING.LEVELS.EDIT'),
    help: t('KANBAN_SHARING.LEVEL_HELP.EDIT'),
  },
]);
const levelIds = ['view', 'comment', 'edit'];

const responsePayload = response =>
  response?.data?.payload ?? response?.data ?? {};
const cloneAccesses = accesses =>
  (Array.isArray(accesses) ? accesses : []).map(access => ({
    ...access,
    user_id: Number(access.user_id),
    access_level: levelIds.includes(access.access_level)
      ? access.access_level
      : 'view',
  }));

const availableAgents = computed(() => {
  const selectedIds = new Set(
    draftAccesses.value.map(access => access.user_id)
  );
  return accountAgents.value.filter(
    agent => !selectedIds.has(Number(agent.id))
  );
});

const normalizedShareUrl = computed(() => {
  if (!shareUrl.value) return '';
  const url = new URL(shareUrl.value, window.location.origin);
  if (isWorkspaceV2Path(window.location.pathname)) {
    url.pathname = workspaceV2Path(url.pathname);
  }
  return url.toString();
});

const accessFingerprint = accesses =>
  accesses
    .map(access => `${Number(access.user_id)}:${access.access_level}`)
    .sort()
    .join('|');

const hasChanges = computed(
  () =>
    accessFingerprint(originalAccesses.value) !==
    accessFingerprint(draftAccesses.value)
);

const selectedLevelHelp = computed(
  () =>
    levels.value.find(level => level.id === selectedAccessLevel.value)?.help ||
    ''
);

const errorMessage = computed(() => {
  if (errorKey.value === 'KANBAN_SHARING.ERRORS.REVOKED') {
    return t('KANBAN_SHARING.ERRORS.REVOKED');
  }
  if (errorKey.value === 'KANBAN_SHARING.ERRORS.NOT_FOUND') {
    return t('KANBAN_SHARING.ERRORS.NOT_FOUND');
  }
  return t('KANBAN_SHARING.ERRORS.LOAD');
});

const operationErrorMessage = computed(() =>
  operationErrorKey.value ? t('KANBAN_SHARING.ERRORS.SAVE') : ''
);

const copyStateMessage = computed(() => {
  if (copyState.value === 'copied') return t('KANBAN_SHARING.LINK.COPIED');
  if (copyState.value === 'failed') return t('KANBAN_SHARING.LINK.FAILED');
  return '';
});

const applyEnvelope = envelope => {
  shareUrl.value = envelope.share_url || '';
  canManage.value = envelope.can_manage === true;
  originalAccesses.value = cloneAccesses(envelope.shared_accesses);
  draftAccesses.value = cloneAccesses(envelope.shared_accesses);
};

const load = async () => {
  isLoading.value = true;
  errorKey.value = '';
  operationErrorKey.value = '';
  copyState.value = '';
  try {
    const [accessResponse, agentsResponse] = await Promise.all([
      kanbanSharedAccessAPI.get(props.itemId),
      agentsAPI.get(),
    ]);
    applyEnvelope(responsePayload(accessResponse));
    const agentsPayload = responsePayload(agentsResponse);
    accountAgents.value = Array.isArray(agentsPayload) ? agentsPayload : [];
  } catch (error) {
    errorKey.value = kanbanAccessErrorKey(error);
  } finally {
    isLoading.value = false;
  }
};

const open = () => {
  selectedUserId.value = '';
  selectedAccessLevel.value = 'comment';
  dialogRef.value?.open();
  load();
};

const close = () => dialogRef.value?.close();

const addSelectedAgent = () => {
  const agent = accountAgents.value.find(
    candidate => Number(candidate.id) === Number(selectedUserId.value)
  );
  if (!agent) return;

  draftAccesses.value.push({
    id: null,
    user_id: Number(agent.id),
    access_level: selectedAccessLevel.value,
    user: agent,
  });
  selectedUserId.value = '';
  selectedAccessLevel.value = 'comment';
};

const revoke = userId => {
  draftAccesses.value = draftAccesses.value.filter(
    access => Number(access.user_id) !== Number(userId)
  );
};

const save = async () => {
  if (!hasChanges.value || isSaving.value) return;
  isSaving.value = true;
  errorKey.value = '';
  operationErrorKey.value = '';

  const originalByUser = new Map(
    originalAccesses.value.map(access => [Number(access.user_id), access])
  );
  const draftByUser = new Map(
    draftAccesses.value.map(access => [Number(access.user_id), access])
  );

  const requests = [];
  originalByUser.forEach((access, userId) => {
    if (!draftByUser.has(userId)) {
      requests.push(kanbanSharedAccessAPI.delete(props.itemId, access.id));
    }
  });
  draftByUser.forEach((access, userId) => {
    const original = originalByUser.get(userId);
    if (!original) {
      requests.push(
        kanbanSharedAccessAPI.create(props.itemId, {
          user_id: userId,
          access_level: access.access_level,
        })
      );
    } else if (original.access_level !== access.access_level) {
      requests.push(
        kanbanSharedAccessAPI.update(props.itemId, original.id, {
          access_level: access.access_level,
        })
      );
    }
  });

  try {
    await Promise.all(requests);
    await load();
    emit('updated', cloneAccesses(originalAccesses.value));
    close();
  } catch {
    await load();
    if (!errorKey.value) {
      operationErrorKey.value = 'KANBAN_SHARING.ERRORS.SAVE';
    }
  } finally {
    isSaving.value = false;
  }
};

const copyLink = async () => {
  if (!normalizedShareUrl.value || isCopying.value) return;
  isCopying.value = true;
  copyState.value = '';
  try {
    await copyTextToClipboard(normalizedShareUrl.value);
    copyState.value = 'copied';
    useAlert(t('KANBAN_SHARING.LINK.COPIED'));
  } catch {
    copyState.value = 'failed';
    useAlert(t('KANBAN_SHARING.LINK.FAILED'));
  } finally {
    isCopying.value = false;
  }
};

defineExpose({ open, close, load });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('KANBAN_SHARING.DIALOG.TITLE')"
    :description="itemTitle || t('KANBAN_SHARING.DIALOG.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div
      v-if="isLoading"
      class="flex min-h-52 items-center justify-center text-sm text-n-slate-11"
      role="status"
    >
      <span class="i-ph-spinner-gap mr-2 size-5 animate-spin" />
      {{ t('KANBAN_SHARING.STATES.LOADING') }}
    </div>

    <div
      v-else-if="errorKey"
      class="rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-4 text-sm text-n-ruby-11"
      role="alert"
    >
      <p class="m-0">{{ errorMessage }}</p>
      <Button
        v-if="errorKey === 'KANBAN_SHARING.ERRORS.LOAD'"
        class="mt-3"
        color="slate"
        variant="outline"
        size="sm"
        :label="t('KANBAN_SHARING.ACTIONS.RETRY')"
        @click="load"
      />
    </div>

    <div v-else-if="!canManage" class="space-y-4">
      <div
        class="rounded-xl border border-n-amber-6 bg-n-amber-3 p-4 text-sm text-n-amber-11"
        role="alert"
      >
        {{ t('KANBAN_SHARING.ERRORS.CANNOT_MANAGE') }}
      </div>
    </div>

    <div v-else class="space-y-5">
      <p
        v-if="operationErrorKey"
        class="m-0 rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ operationErrorMessage }}
      </p>
      <section class="space-y-3" aria-labelledby="share-add-agent-title">
        <h4
          id="share-add-agent-title"
          class="m-0 text-sm font-medium text-n-slate-12"
        >
          {{ t('KANBAN_SHARING.DIALOG.ADD_AGENT') }}
        </h4>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_10rem_auto]">
          <select
            v-model="selectedUserId"
            class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :aria-label="t('KANBAN_SHARING.DIALOG.AGENT')"
          >
            <option value="">
              {{ t('KANBAN_SHARING.DIALOG.SELECT_AGENT') }}
            </option>
            <option
              v-for="agent in availableAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
          <select
            v-model="selectedAccessLevel"
            class="h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
            :aria-label="t('KANBAN_SHARING.DIALOG.PERMISSION')"
          >
            <option v-for="level in levels" :key="level.id" :value="level.id">
              {{ level.label }}
            </option>
          </select>
          <Button
            data-testid="kanban-share-add-agent"
            type="button"
            color="slate"
            variant="outline"
            :disabled="!selectedUserId"
            :label="t('KANBAN_SHARING.ACTIONS.ADD')"
            @click="addSelectedAgent"
          />
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ selectedLevelHelp }}
        </p>
      </section>

      <section class="space-y-2" aria-labelledby="share-current-access-title">
        <h4
          id="share-current-access-title"
          class="m-0 text-sm font-medium text-n-slate-12"
        >
          {{ t('KANBAN_SHARING.DIALOG.CURRENT_ACCESS') }}
        </h4>
        <p
          v-if="!draftAccesses.length"
          class="m-0 rounded-xl bg-n-alpha-2 p-3 text-sm text-n-slate-11"
        >
          {{ t('KANBAN_SHARING.DIALOG.NO_SHARED_ACCESS') }}
        </p>
        <div
          v-for="access in draftAccesses"
          :key="access.user_id"
          class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak p-3"
        >
          <Avatar
            :name="
              access.user?.name || access.user?.email || String(access.user_id)
            "
            :src="access.user?.avatar_url || ''"
            :size="32"
            rounded-full
          />
          <div class="min-w-32 flex-1">
            <p class="m-0 truncate text-sm font-medium text-n-slate-12">
              {{ access.user?.name || access.user?.email }}
            </p>
            <p class="m-0 truncate text-xs text-n-slate-11">
              {{ access.user?.email }}
            </p>
          </div>
          <select
            v-model="access.access_level"
            class="h-9 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-sm text-n-slate-12"
            :aria-label="
              t('KANBAN_SHARING.DIALOG.PERMISSION_FOR', {
                name: access.user?.name,
              })
            "
          >
            <option v-for="level in levels" :key="level.id" :value="level.id">
              {{ level.label }}
            </option>
          </select>
          <Button
            :data-testid="`kanban-share-revoke-${access.user_id}`"
            type="button"
            color="ruby"
            variant="ghost"
            size="sm"
            :label="t('KANBAN_SHARING.ACTIONS.REVOKE')"
            :aria-label="
              t('KANBAN_SHARING.ACTIONS.REVOKE_FOR', {
                name: access.user?.name,
              })
            "
            @click="revoke(access.user_id)"
          />
        </div>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-alpha-2 p-3">
        <h4
          class="m-0 flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <span class="i-ph-lock-key size-4" />
          {{ t('KANBAN_SHARING.LINK.TITLE') }}
        </h4>
        <p class="mb-2 mt-1 text-xs leading-5 text-n-slate-11">
          {{ t('KANBAN_SHARING.LINK.DESCRIPTION') }}
        </p>
        <div class="flex gap-2">
          <input
            :value="normalizedShareUrl"
            readonly
            class="h-9 min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-1 px-3 text-xs text-n-slate-11"
            :aria-label="t('KANBAN_SHARING.LINK.FIELD_LABEL')"
            @focus="$event.target.select()"
          />
          <Button
            data-testid="kanban-share-copy-link"
            type="button"
            color="slate"
            variant="outline"
            size="sm"
            icon="i-ph-link"
            :is-loading="isCopying"
            :disabled="!normalizedShareUrl"
            :label="t('KANBAN_SHARING.ACTIONS.COPY_LINK')"
            @click="copyLink"
          />
        </div>
        <p
          v-if="copyState"
          class="mb-0 mt-2 text-xs"
          :class="copyState === 'copied' ? 'text-n-teal-11' : 'text-n-ruby-11'"
          role="status"
        >
          {{ copyStateMessage }}
        </p>
      </section>

      <div
        class="rounded-xl border border-n-blue-6 bg-n-blue-3 p-3 text-xs leading-5 text-n-blue-11"
      >
        {{ t('KANBAN_SHARING.DIALOG.SCOPE_NOTICE') }}
      </div>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-end gap-2">
        <Button
          data-testid="kanban-share-cancel"
          type="button"
          color="slate"
          variant="faded"
          :disabled="isSaving"
          :label="t('KANBAN_SHARING.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          v-if="canManage && !isLoading && !errorKey"
          data-testid="kanban-share-save"
          type="button"
          :is-loading="isSaving"
          :disabled="!hasChanges || isSaving"
          :label="t('KANBAN_SHARING.ACTIONS.SAVE')"
          @click="save"
        />
      </div>
    </template>
  </Dialog>
</template>
